import type { MetricDefinition, TrendPeriod } from './types'

/**
 * Pure rules for metric trend periods and intraday capture. No db, so the
 * ladder, fill rule and capture schedule are unit-testable.
 */

export const TREND_PERIODS = [
  '24h',
  '7d',
  '30d',
  '90d'
] as const satisfies readonly TrendPeriod[]

export const MINUTE_MS = 60_000
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

export const PERIOD_MS: Record<TrendPeriod, number> = {
  '24h': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
  '90d': 90 * DAY_MS
}

/** An event metric's bucket width per period, so each line has 24-90 points. */
export const EVENT_BUCKET_MINUTES: Record<TrendPeriod, number> = {
  '24h': 60,
  '7d': 240,
  '30d': 1440,
  '90d': 1440
}

/** A rung is filled once history reaches this share of its span. */
const FILL_SHARE = 0.9

function isTrendPeriod(value: unknown): value is TrendPeriod {
  return TREND_PERIODS.includes(value as TrendPeriod)
}

type TrendFacts = Pick<MetricDefinition, 'capture' | 'trendDefault' | 'area'>

/**
 * The period a metric opens on: operational numbers move in hours, sales,
 * finance and project numbers in weeks. `hasBuckets` is true for event-count
 * metrics, which have real hourly history without capture.
 */
export function suggestedTrendPeriod(
  metric: TrendFacts,
  hasBuckets: boolean
): TrendPeriod {
  if (metric.trendDefault) return metric.trendDefault
  if (metric.capture || hasBuckets) return '24h'
  return ['finance', 'sales', 'projects'].includes(metric.area) ? '90d' : '30d'
}

/** A requested period if it is one the metric offers, else the suggestion. */
export function resolveRequestedPeriod(
  raw: string | null,
  offered: readonly TrendPeriod[],
  suggested: TrendPeriod
): TrendPeriod {
  return isTrendPeriod(raw) && offered.includes(raw) ? raw : suggested
}

/** An explicit choice if the metric still offers it, else the suggestion. */
export function effectiveTrendPeriod(
  explicit: TrendPeriod | undefined,
  offered: readonly TrendPeriod[],
  suggested: TrendPeriod
): TrendPeriod {
  return explicit && offered.includes(explicit) ? explicit : suggested
}

/** `24h` needs real sub-daily history: captured levels and event counts. */
export function offeredTrendPeriods(
  metric: Pick<MetricDefinition, 'capture'>,
  hasBuckets: boolean
): TrendPeriod[] {
  return metric.capture || hasBuckets
    ? [...TREND_PERIODS]
    : TREND_PERIODS.slice(1)
}

/**
 * The rungs to try for a requested period: it first, then each smaller one the
 * metric offers.
 */
export function ladderFor(
  requested: TrendPeriod,
  offered: readonly TrendPeriod[]
): TrendPeriod[] {
  return TREND_PERIODS.filter(
    (p) => offered.includes(p) && PERIOD_MS[p] <= PERIOD_MS[requested]
  ).reverse()
}

/** History fills a rung when its oldest point is 90% of the span old. */
export function isRungFilled(
  oldest: Date | null,
  period: TrendPeriod,
  now: Date
): boolean {
  return (
    oldest !== null &&
    now.getTime() - oldest.getTime() >= FILL_SHARE * PERIOD_MS[period]
  )
}

/** The first rung of the ladder that history fills, or null. */
export function pickRung(
  ladder: readonly TrendPeriod[],
  oldestFor: (rung: TrendPeriod) => Date | null,
  now: Date
): TrendPeriod | null {
  return ladder.find((rung) => isRungFilled(oldestFor(rung), rung, now)) ?? null
}

/** `YYYY-MM-DD` (UTC) on which `requested` is fully covered by history. */
export function fullFromDate(
  oldest: Date | null,
  requested: TrendPeriod
): string | null {
  if (!oldest) return null
  return new Date(oldest.getTime() + PERIOD_MS[requested])
    .toISOString()
    .slice(0, 10)
}

/**
 * Slot starts on a `stepMs` grid aligned to the UTC epoch, covering the
 * `count` slots that end with the one containing `now`.
 */
export function slotGrid(now: Date, stepMs: number, count: number): Date[] {
  const last = Math.floor(now.getTime() / stepMs) * stepMs
  return Array.from(
    { length: count },
    (_, i) => new Date(last - (count - 1 - i) * stepMs)
  )
}

export interface TrendPoint {
  at: string
  value: number | null
}

/** A grid filled from captures; a slot with none is a gap (null), not zero. */
export function fillGrid(
  grid: readonly Date[],
  values: ReadonlyMap<number, number>
): TrendPoint[] {
  return grid.map((at) => ({
    at: at.toISOString(),
    value: values.get(at.getTime()) ?? null
  }))
}

/**
 * Event counts per bucket across `[from, to)` on the epoch-aligned grid. A
 * bucket with no events is a real zero, never a gap.
 */
export function zeroFillBuckets(
  counts: ReadonlyMap<number, number>,
  from: Date,
  to: Date,
  minutes: number
): { at: string; value: number }[] {
  const step = minutes * MINUTE_MS
  const out: { at: string; value: number }[] = []
  for (
    let t = Math.floor(from.getTime() / step) * step;
    t < to.getTime();
    t += step
  ) {
    out.push({ at: new Date(t).toISOString(), value: counts.get(t) ?? 0 })
  }
  return out
}

/** Drop leading gaps: a line begins where its history does. */
export function trimLeadingGaps(points: readonly TrendPoint[]): TrendPoint[] {
  const first = points.findIndex((p) => p.value !== null)
  return first < 0 ? [] : points.slice(first)
}

/**
 * Ends a level line on the live value. The live point replaces the slot that
 * contains `now` (and any later one) instead of doubling it.
 */
export function withLiveValue(
  points: readonly TrendPoint[],
  live: number | null,
  now: Date,
  stepMs: number
): TrendPoint[] {
  if (live === null) return [...points]
  const slotStart = Math.floor(now.getTime() / stepMs) * stepMs
  const kept = points.filter((p) => Date.parse(p.at) < slotStart)
  return [...kept, { at: now.toISOString(), value: live }]
}

/**
 * Whether a cached live value may end a captured metric's line: not stale, and
 * computed no earlier than the newest capture was written. Otherwise it would
 * replace a fresher number with an older one.
 */
export function liveMayEndLine(
  stale: boolean,
  computedAt: Date,
  newestCaptureAt: Date | null
): boolean {
  return !stale && (!newestCaptureAt || computedAt >= newestCaptureAt)
}

/** Midnight UTC of a `YYYY-MM-DD` day, as the instant daily points carry. */
export const dayStart = (day: string) => new Date(`${day}T00:00:00.000Z`)

// ---------------------------------------------------------------------------
// Intraday capture schedule.
// ---------------------------------------------------------------------------

const CAPTURE_SLOT_MINUTES = 15

/**
 * The slot a capture run belongs to: `now` rounded to the NEAREST 15 minutes
 * (UTC). The scheduler fires three minutes before the phones' quarter-hour
 * widget refresh so the value lands first; rounding keeps that early run, and a
 * slightly late one, in the quarter hour it is for.
 */
export function intradaySlot(now: Date): Date {
  const step = CAPTURE_SLOT_MINUTES * MINUTE_MS
  return new Date(Math.round(now.getTime() / step) * step)
}

/** Whether a metric captured every `everyMinutes` is due in this slot. */
export function isCaptureDue(slot: Date, everyMinutes: number): boolean {
  const minuteOfDay = slot.getUTCHours() * 60 + slot.getUTCMinutes()
  return minuteOfDay % everyMinutes === 0
}

/**
 * Retention: past `hourlyOnlyBefore` only top-of-hour rows are kept, past
 * `dropBefore` none. 15-minute detail serves the 24h line; 7d and later read
 * hourly.
 */
export function intradayRetention(now: Date) {
  return {
    hourlyOnlyBefore: new Date(now.getTime() - 7 * DAY_MS),
    dropBefore: new Date(now.getTime() - 90 * DAY_MS)
  }
}
